<template>
  <el-dialog
    title=""
    width="520px"
    :visible.sync="innerVisible"
    :close-on-click-modal="false"
    :show-close="false"
    custom-class="config-fee-dialog"
    center
  >
    <div class="dialog-header">
      <div class="header-icon">
        <i class="el-icon-s-operation"></i>
      </div>
      <div class="header-info">
        <div class="header-title">{{ title }}</div>
        <div class="header-desc">为参与「{{ activityName || defaultActivityName }}」的用户赠送平台服务费</div>
      </div>
      <i class="el-icon-close close-icon" @click="innerVisible = false"></i>
    </div>

    <div class="dialog-body">
      <div class="info-section">
        <div class="section-label">用户信息</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">用户姓名</span>
            <span class="info-value">{{ userName || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">手机号</span>
            <span class="info-value">{{ displayMobile }}</span>
          </div>
          <div class="info-item full-width">
            <span class="info-label">参与活动名称</span>
            <span class="info-value">{{ activityName || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="config-section">
        <div class="section-label">配置信息</div>

        <div class="tip-box">
          <i class="el-icon-info"></i>
          <span>配置后用户将在配置月份无需支付服务费, 正常使用。</span>
        </div>
        <div class="warning-text">
          <i class="el-icon-warning-outline"></i>
          <span>配置后不可撤销</span>
        </div>

        <el-form
          :model="form"
          :rules="rules"
          ref="feeConfigForm"
          label-width="130px"
          label-position="left"
          size="small"
          class="config-form"
        >
          <el-form-item :label="giftTypeLabel" prop="giftType">
            <!-- 锁定态（小红书入口）：只有一项、禁用，运营无从选其他类型 -->
            <el-select
              v-model="form.giftType"
              :placeholder="'请选择' + giftTypeLabel"
              :disabled="isLocked"
              style="width: 100%"
            >
              <el-option
                v-for="item in giftTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <div v-if="isLocked" class="source-lock-tip">{{ lockTip }}</div>
          </el-form-item>
          <el-form-item :label="monthsLabel" prop="giftMonths">
            <el-input
              v-model="form.giftMonths"
              type="number"
              placeholder="请输入月数"
            >
              <template slot="append">个月</template>
            </el-input>
          </el-form-item>
        </el-form>

        <div class="cost-row">
          <div class="cost-left">
            <div class="cost-label">赠送服务费金额</div>
            <div class="cost-value">¥{{ totalGiftAmount }}<span class="cost-unit">元</span></div>
          </div>
          <div class="cost-right">
            <div class="cost-hint">每月服务费固定{{ effectiveUnitPrice }}元</div>
            <div class="cost-hint">赠送金额 = 配置月数 × {{ effectiveUnitPrice }}元</div>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="innerVisible = false" size="small">取 消</el-button>
      <el-button
        type="primary"
        @click="submit"
        size="small"
        :loading="submitting"
        class="confirm-btn"
      >{{ confirmText }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
/**
 * 单价保底值（元/月）：后端没返回单价（或返回非数字）时用，避免弹窗金额算成 ¥0 或 NaN。
 * 后端返回的是活动定义价 t_activity_extend.activity_data_purchase_price，与发放落库同源。
 */
const FALLBACK_UNIT_PRICE = 6;

/**
 * 配置平台服务费弹窗（共享组件）。
 *
 * <p>两个入口共用同一份弹窗与文案：</p>
 * <ul>
 *   <li><b>平台服务费页签</b>（订单详情）：配置类型可自由选「售后赠送 / 活动赠送」，不传
 *       {@code lockGiftType}。</li>
 *   <li><b>小红书活动发放入口</b>：传 {@code lockGiftType="ACTIVITY_GIFT"} +
 *       {@code presetMonths=预期奖励月数} —— 弹出来只有「活动赠送」一项且禁用、月数进来就是预期值
 *       （后台原型：来源固定为「活动赠送」，不可修改）。</li>
 * </ul>
 *
 * <p><b>提交只 emit，不自己发请求</b>：两个入口打的是不同接口（平台侧 /axbApp/giftTraffic、
 * 小红书侧 /xhsActivity/admin/grant），由父组件决定，避免在本组件里塞 if 分支。</p>
 *
 * <p>用法：{@code <FeeConfigDialog :visible.sync="v" :user-name="row.userName" ... @submit="doSubmit" />}</p>
 *
 * <p>赠送金额 = 配置月数 × 单价；单价取传入的 {@code unitPrice}（后端返回的活动定义价，与发放落库同源），
 * 取不到时保底 {@code FALLBACK_UNIT_PRICE} 元/月。</p>
 */
export default {
  name: 'FeeConfigDialog',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '配置服务费' },
    confirmText: { type: String, default: '确认配置' },
    userName: { type: String, default: '' },
    userMobile: { type: String, default: '' },
    /** 手机号是否掩码显示；小红书侧 PRD 要求明文不脱敏，传 false */
    maskMobile: { type: Boolean, default: true },
    activityName: { type: String, default: '' },
    defaultActivityName: { type: String, default: '安心包598活动-用桩服务' },
    giftTypeLabel: { type: String, default: '配置类型' },
    monthsLabel: { type: String, default: '服务费配置周期' },
    /** 非空即锁定：下拉只有这一项且禁用 */
    lockGiftType: { type: String, default: '' },
    lockTip: { type: String, default: '来源由「小红书笔记奖励」入口锁定，不可修改' },
    /** 打开时预填的月数（小红书入口传预期奖励月数） */
    presetMonths: { type: [Number, String], default: '' },
    /** 每月服务费单价（元/月），后端返回的活动定义价；不传/非数字时保底 FALLBACK_UNIT_PRICE */
    unitPrice: { type: Number, default: null },
    submitting: { type: Boolean, default: false },
  },
  data() {
    return {
      giftTypeAll: [
        { value: 'AFTER_SALE_GIFT', label: '售后赠送' },
        { value: 'ACTIVITY_GIFT', label: '活动赠送' },
      ],
      form: { giftType: '', giftMonths: '' },
      rules: {
        giftType: [{ required: true, message: '请选择配置类型', trigger: 'change' }],
        giftMonths: [
          { required: true, message: '请输入月数', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (value === '' || value === null || value === undefined) {
                callback(new Error('请输入月数'));
              } else if (!Number.isInteger(num) || num <= 0) {
                callback(new Error('请输入大于0的整数'));
              } else {
                callback();
              }
            },
            trigger: 'blur',
          },
        ],
      },
    };
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },
    isLocked() {
      return !!this.lockGiftType;
    },
    giftTypeOptions() {
      return this.isLocked
        ? this.giftTypeAll.filter((i) => i.value === this.lockGiftType)
        : this.giftTypeAll;
    },
    displayMobile() {
      const mobile = this.userMobile || '';
      if (!mobile) return '-';
      if (!this.maskMobile || mobile.length < 7) return mobile;
      return mobile.replace(mobile.substring(3, 7), '****');
    },
    /**
     * 单价：后端（活动定义价）传了就用传的（含 0，保持平台页签原口径）；
     * 没传/拿到非数字时保底 {@link FeeConfigDialog.FALLBACK_UNIT_PRICE} 元/月，
     * 保证弹窗金额永远能算出来（不显示 ¥0 或 NaN）。
     */
    effectiveUnitPrice() {
      const p = this.unitPrice;
      if (p === null || p === undefined || p === '') {
        return FALLBACK_UNIT_PRICE;
      }
      const n = Number(p);
      return Number.isNaN(n) ? FALLBACK_UNIT_PRICE : n;
    },
    totalGiftAmount() {
      const months = Number(this.form.giftMonths) || 0;
      return (months * this.effectiveUnitPrice).toFixed(2);
    },
  },
  watch: {
    visible(val) {
      if (val) this.initForm();
    },
  },
  methods: {
    initForm() {
      this.form = {
        // 锁定态直接落锁定值，避免下拉留空导致校验报错
        giftType: this.lockGiftType || '',
        giftMonths: this.presetMonths === null || this.presetMonths === undefined
          ? ''
          : String(this.presetMonths),
      };
      this.$nextTick(() => {
        if (this.$refs.feeConfigForm) this.$refs.feeConfigForm.clearValidate();
      });
    },
    submit() {
      this.$refs.feeConfigForm.validate((valid) => {
        if (!valid) return;
        this.$emit('submit', {
          giftType: this.form.giftType,
          giftMonths: Number(this.form.giftMonths),
        });
      });
    },
  },
};
</script>

<style scoped>
/* 弹窗样式 */
.config-fee-dialog {
  border-radius: 12px;
  overflow: hidden;
  padding: 0;
}

::v-deep .config-fee-dialog .el-dialog__body {
  padding: 0;
}

::v-deep .config-fee-dialog .el-dialog__header {
  display: none;
}

::v-deep .config-fee-dialog .el-dialog__footer {
  padding: 16px 24px 20px;
  border-top: none;
  text-align: right;
}

.dialog-header {
  background: linear-gradient(135deg, #5b8def 0%, #6ba3f7 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  color: #fff;
  position: relative;
}

.header-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;

  i {
    font-size: 20px;
    color: #fff;
  }
}

.header-info {
  flex: 1;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.header-desc {
  font-size: 12px;
  opacity: 0.92;
}

.close-icon {
  font-size: 18px;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}

.dialog-body {
  padding: 0;
}

.info-section {
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #f0f2f5;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 14px;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    display: inline-block;
    width: 3px;
    height: 13px;
    background: #5b8def;
    border-radius: 2px;
    margin-right: 8px;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 24px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.full-width {
    grid-column: 1 / -1;
  }
}

.info-label {
  font-size: 12px;
  color: #999;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.config-section {
  padding: 16px 24px 20px;
  background: #fff;
}

.tip-box {
  background: #eef4ff;
  border: 1px solid #dbe6ff;
  border-radius: 6px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4a6cf7;
  margin-bottom: 8px;

  i {
    font-size: 15px;
    flex-shrink: 0;
  }
}

.warning-text {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  font-size: 13px;
  color: #e6573c;
  margin-bottom: 16px;
  width: 100%;
  text-align: left;

  i {
    font-size: 14px;
  }
}

.config-form {
  .el-form-item {
    margin-bottom: 14px;
  }

  .el-form-item__label {
    font-size: 13px;
    color: #606266;
    font-weight: normal;
  }
}

.source-lock-tip {
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: #8a94a3;
}

.cost-row {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  border: 1px solid #ebeef5;
}

.cost-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cost-label {
  font-size: 12px;
  color: #999;
}

.cost-value {
  font-size: 22px;
  font-weight: 700;
  color: #ff6034;

  .cost-unit {
    font-size: 13px;
    font-weight: normal;
    margin-left: 2px;
    color: #ff6034;
  }
}

.cost-right {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-end;
}

.cost-hint {
  font-size: 12px;
  color: #999;
}

.confirm-btn {
  background: linear-gradient(135deg, #5b8def 0%, #6ba3f7 100%) !important;
  border: none !important;
  border-radius: 6px;
}
</style>
